import { CSSProperties, FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { LuckyGameData } from './types';
import { Campaign, GameResult } from '../../types';
import { TApiError } from '../../components';
import { getLuckyPrizeImage, getWidgetBgDesktopImage, getWidgetBgMobileImage, useWindowResize } from '../../lib';
import { GameWidgetWrapper, GameFormWrapper, ResultScreen } from '../common';
import { Form } from '../common/form';
import { getSlides } from './get-slides';
import styled from 'styled-components';
import { isTextField } from '../../components/form-editor/text-field';
import { Arrow } from './arrow';

import './styles.css';
import { getCouponDisplayName } from '../common/utils';

const POINTS = [2000, 3500];
const EPS = 50;
const PX_PER_FRAME = 2;
const DESKTOP_GAP = 48;
const MOBILE_GAP = 24;

type LuckyProps = {
  campaign: Campaign;
  gameData: LuckyGameData;
  isDesktop?: boolean;
  formEditor?: ReactNode;
  resultScreenEditor?: ReactNode;
  gameResult?: GameResult;
  gameResultFetching: boolean;
  gameResultError?: TApiError;
  resultScreenVisible: boolean;
  editable: boolean;
  onAnimationEnd: () => void;
  onCloseResultScreen: () => void;
  onButtonClick?: () => void;
};

export const LuckyGame: FC<LuckyProps> = ({
  gameData,
  campaign,
  formEditor,
  resultScreenEditor,
  gameResult,
  resultScreenVisible,
  gameResultError,
  editable,
  gameResultFetching,
  onAnimationEnd,
  onButtonClick,
  onCloseResultScreen,
  ...restProps
}) => {
  const isMediaDesktop = useMediaQuery({ minWidth: 768 });
  const isDesktop = restProps.isDesktop ?? isMediaDesktop;

  const mainSectionRef = useRef<HTMLDivElement>(null);
  const firstSlideSectionRef = useRef<HTMLDivElement>(null);
  const secondSlideSectionRef = useRef<HTMLDivElement>(null);
  const sliderWrapperRef = useRef<HTMLDivElement>(null);

  const [mainSectionWidth, setMainSectionWidth] = useState(0);
  const [slideSectionWidth, setSlideSectionWidth] = useState(0);
  const [prizeWidth, setPrizeWidth] = useState(109);

  const windowWidth = useRef(0);

  const speedRef = useRef(PX_PER_FRAME);
  const dx1 = useRef(0);
  const dx2 = useRef(0);

  const idleRequestId = useRef(0);
  const speedUpRequestId = useRef(0);

  const slides = getSlides(campaign.wixCoupons);

  const updateParams = useCallback(() => {
    windowWidth.current = window.innerWidth;
    const gap = isDesktop ? DESKTOP_GAP : MOBILE_GAP;

    /**
     * 2 * gap + 3 * prize = mainSectionWidth
     * prize = (mainSectionWidth - 2 * gap) / 3
     */

    const prizeWidth = Math.ceil((mainSectionWidth - 2 * gap) / 3);

    setPrizeWidth(prizeWidth);
  }, [mainSectionWidth, isDesktop]);

  useWindowResize(() => {
    if (windowWidth.current !== window.innerWidth && mainSectionRef.current) {
      dx1.current = 0;
      dx2.current = 0;

      setMainSectionWidth(mainSectionRef.current.getBoundingClientRect().width);

      updateParams();
    }
  });

  const handleTransitionEnd = () => {
    setTimeout(() => {
      onAnimationEnd();
    }, 300);
  };

  const setDx1 = useCallback((dx: number) => {
    if (firstSlideSectionRef.current) {
      dx1.current = dx;
      firstSlideSectionRef.current.style.transform = `translate3d(${dx}px, 0, 0)`;
    }
  }, []);

  const setDx2 = useCallback((dx: number) => {
    if (secondSlideSectionRef.current) {
      dx2.current = dx;
      secondSlideSectionRef.current.style.transform = `translate3d(${dx}px, 0, 0)`;
    }
  }, []);

  const rotate = useCallback(
    (result: GameResult) => {
      const start = Date.now();
      const upDx = 0.4;

      const isStartPosition = () => Math.abs(dx1.current) <= EPS;

      const prizeIndex = result.couponIndex || 0;
      const gap = isDesktop ? DESKTOP_GAP : MOBILE_GAP;
      const transform = slideSectionWidth + gap + (prizeWidth + gap) * (prizeIndex - 1);

      const run = () => {
        const timePass = Date.now() - start >= POINTS[1];

        const stop = timePass && isStartPosition();

        if (stop) {
          speedRef.current = 0;
          setDx1(0);
          setDx2(0);

          if (sliderWrapperRef.current) {
            sliderWrapperRef.current.style.transform = `translate3d(-${transform}px, 0, 0)`;
          }
        } else {
          speedUpRequestId.current = window.requestAnimationFrame(run);
        }
      };

      const speedUp = () => {
        if (Date.now() - start <= POINTS[0]) {
          speedRef.current = speedRef.current + upDx;

          speedUpRequestId.current = window.requestAnimationFrame(speedUp);
        } else {
          speedUpRequestId.current = window.requestAnimationFrame(run);
        }
      };

      speedUpRequestId.current = window.requestAnimationFrame(speedUp);

      return () => {
        window.cancelAnimationFrame(speedUpRequestId.current);
      };
    },
    [isDesktop, prizeWidth, setDx1, setDx2, slideSectionWidth]
  );

  useEffect(() => {
    if (mainSectionRef.current) {
      const mainSectionWidth = mainSectionRef.current.getBoundingClientRect().width;

      setMainSectionWidth(mainSectionWidth);
    }

    if (firstSlideSectionRef.current) {
      const slideSectionWidth = firstSlideSectionRef.current.getBoundingClientRect().width;

      setSlideSectionWidth(slideSectionWidth);
    }
  }, [prizeWidth, isDesktop]);

  const move = useCallback(() => {
    if (!firstSlideSectionRef.current || !secondSlideSectionRef.current || !mainSectionRef.current) {
      idleRequestId.current = window.requestAnimationFrame(move);
      return;
    }

    const gap = isDesktop ? DESKTOP_GAP : MOBILE_GAP;

    const speed = speedRef.current;

    let nextDx1 = Math.floor(dx1.current - speed);
    let nextDx2 = Math.floor(dx2.current - speed);

    if (nextDx1 <= -slideSectionWidth) {
      nextDx1 = Math.floor(slideSectionWidth + speed + gap * 2);
    } else if (nextDx2 <= -slideSectionWidth * 2) {
      nextDx2 = Math.floor(speed + gap * 2);
    }

    setDx1(nextDx1);
    setDx2(nextDx2);

    if (speedRef.current) {
      idleRequestId.current = window.requestAnimationFrame(move);
    }
  }, [setDx1, setDx2, slideSectionWidth, isDesktop]);

  useEffect(() => {
    if (slideSectionWidth && !editable && !resultScreenVisible) {
      idleRequestId.current = window.requestAnimationFrame(move);
    }

    return () => {
      window.cancelAnimationFrame(idleRequestId.current);
    };
  }, [move, slideSectionWidth, editable, resultScreenVisible]);

  useEffect(() => {
    updateParams();
  }, [updateParams]);

  useEffect(() => {
    if (!resultScreenVisible) {
      speedRef.current = PX_PER_FRAME;
    }
  }, [resultScreenVisible]);

  useEffect(() => {
    if (!gameResult) {
      return;
    }

    const { couponIndex } = gameResult;

    if (couponIndex === undefined) {
      onAnimationEnd();
      return;
    }

    rotate(gameResult);
  }, [gameResult, onAnimationEnd, rotate]);

  const onClose = useCallback(() => {
    onCloseResultScreen();
  }, [onCloseResultScreen]);

  const couponTextColor = useMemo(() => {
    let color = '';

    campaign.formFields.fields.forEach((field) => {
      if (!color && isTextField(field)) {
        color = field.color;
      }
    });

    return color || '#fff';
  }, [campaign.formFields.fields]);

  const getWidgetStyles = (): CSSProperties => {
    const props: CSSProperties = {
      backgroundColor: campaign.colors.bg,
    };

    if (campaign.useBgImage) {
      let bgImageUrl = isDesktop
        ? getWidgetBgDesktopImage(campaign.bgImageUrl.desktop)
        : getWidgetBgMobileImage(campaign.bgImageUrl.mobile || campaign.bgImageUrl.desktop);

      props.backgroundImage = `url(${
        isDesktop ? getWidgetBgDesktopImage(bgImageUrl) : getWidgetBgMobileImage(bgImageUrl)
      })`;
    }

    return props;
  };

  const renderPrizes = () =>
    slides.map((coupon, index) => {
      const inactive = coupon.deleted || !coupon.active;
      const backgroundImage = getLuckyPrizeImage(coupon.imageSrc || gameData.defaultImageSrc);

      return (
        <Prize key={`${index}-${coupon.uuid}`} style={{ width: `${prizeWidth}px` }}>
          <PrizeImage
            style={{
              backgroundImage: `url(${backgroundImage})`,
              height: `${prizeWidth}px`,
              opacity: inactive ? 0.25 : 1,
            }}
          />

          <PrizeTitle style={{ color: couponTextColor }}>{inactive ? '' : getCouponDisplayName(coupon)}</PrizeTitle>
        </Prize>
      );
    });

  const isButtonLoading = Boolean(gameResultFetching || gameResult);

  return (
    <GameWidgetWrapper style={getWidgetStyles()}>
      {!resultScreenVisible && (
        <>
          <LuckyWrapper ref={mainSectionRef} className="eg-lucky-wrapper">
            <ArrowWrapper>
              <Arrow color={gameData.colors.primary} />
            </ArrowWrapper>

            <SliderWrapper ref={sliderWrapperRef} onTransitionEnd={handleTransitionEnd} className="eg-slider-wrapper">
              <SlideSection ref={firstSlideSectionRef} className="eg-slide-section">
                {renderPrizes()}
              </SlideSection>

              <SlideSection ref={secondSlideSectionRef} className="eg-slide-section">
                {renderPrizes()}
              </SlideSection>
            </SliderWrapper>

            <ArrowWrapper rotated>
              <Arrow color={gameData.colors.primary} />
            </ArrowWrapper>
          </LuckyWrapper>

          <GameFormWrapper>
            {formEditor || (
              <Form
                onButtonClick={onButtonClick}
                buttonDisabled={isButtonLoading}
                buttonLoading={isButtonLoading}
                gameResultError={gameResultError}
              />
            )}
          </GameFormWrapper>
        </>
      )}

      {resultScreenEditor || (
        <ResultScreen
          visible={resultScreenVisible}
          gameResult={gameResult}
          onClose={onClose}
          gameResultError={gameResultError}
        />
      )}
    </GameWidgetWrapper>
  );
};

const LuckyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  margin-top: 32px;
  margin-bottom: 16px;

  @media screen and (min-width: 768px) {
    width: 620px;
    margin: 0;
  }
`;

const SliderWrapper = styled.div`
  display: flex;
  position: relative;
  will-change: transform;
  transform: translate3d(0, 0, 0);
  transition: transform 3s cubic-bezier(0.25, 0.64, 0.28, 1);
  pointer-events: none;
  user-select: none;
  gap: 24px;

  @media screen and (min-width: 768px) {
    transition: transform 2s cubic-bezier(0.25, 0.64, 0.28, 1);
    gap: 48px;
  }
`;

const SlideSection = styled.div`
  display: flex;
  will-change: transform;
  gap: 24px;

  @media screen and (min-width: 768px) {
    gap: 48px;
  }
`;

const Prize = styled.div`
  flex-shrink: 0;
  width: 0;
  outline: none;
`;

const PrizeImage = styled.div`
  width: 100%;
  flex-shrink: 0;
  margin: 0 auto 16px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
`;

const PrizeTitle = styled.div`
  padding: 0 4px;
  text-align: center;
  font-size: 14px;
  line-height: 16px;
  user-select: none;
`;

const ArrowWrapper = styled.div<{ rotated?: boolean }>`
  width: 85%;
  align-self: center;
  margin-bottom: 24px;

  ${(p) =>
    p.rotated &&
    `
    transform: rotate(180deg);
    margin-top: 24px;
    margin-bottom: 0;
  `};
`;
